import {HTMLProps, useCallback, useEffect, useRef, useState} from "react";

import {type Placement, useClick, useDismiss, useInteractions} from "@floating-ui/react";
import {useSnapshot} from "valtio";

import {useFloatingDropdown} from "@/shared/hooks";
import {type DateRangePickerInstance} from "@/shared/models/form/datepickers";
import {type CalendarDay} from "@/shared/ui/form/datepickers/parts";

interface UseDateRangePickerProps {
    model: DateRangePickerInstance;
    controlledIsOpen?: boolean;
    onOpenChange?: (isOpen: boolean) => void;
    disableClickOutside?: boolean;
    isLoading?: boolean;
    placement?: Placement;
    fullscreenOnMobile?: boolean;
    enableAutoScroll?: boolean;
    dropdownOffset?: number;
}

interface UseDateRangePickerReturn {
    snapshot: ReturnType<typeof useSnapshot<DateRangePickerInstance>>;
    isOpen: boolean;
    refs: ReturnType<typeof useFloatingDropdown>["refs"];
    floatingStyles: ReturnType<typeof useFloatingDropdown>["floatingStyles"];
    getReferenceProps: (userProps?: HTMLProps<Element>) => Record<string, unknown>;
    getFloatingProps: (userProps?: HTMLProps<HTMLElement>) => Record<string, unknown>;
    handleDayClick: (day: CalendarDay) => void;
    handleDayHover: (date: Date) => void;
    handleDayLeave: () => void;
    handleClear: () => void;
    formatDisplayValue: (date: Date | null) => string;
}

export function useDateRangePicker({
    model,
    controlledIsOpen,
    onOpenChange,
    disableClickOutside = false,
    isLoading = false,
    placement = "bottom",
    enableAutoScroll = false,
    dropdownOffset,
}: UseDateRangePickerProps): UseDateRangePickerReturn {
    const snapshot = useSnapshot(model);
    const [internalIsOpen, setInternalIsOpen] = useState(false);
    const prevHasValueRef = useRef(snapshot.hasValue);
    // Use controlled or uncontrolled state
    const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;
    const setIsOpen = (value: boolean): void => {
        if (controlledIsOpen === undefined) {
            setInternalIsOpen(value);
        }
        onOpenChange?.(value);
    };

    const {refs, floatingStyles, context} = useFloatingDropdown({
        isOpen,
        onOpenChange: setIsOpen,
        placement,
        enableAutoScroll,
        offsetValue: dropdownOffset,
    });

    // Interactions (click to toggle, dismiss on outside click)
    // Disable click interaction when component is controlled externally
    const isControlled = controlledIsOpen !== undefined;

    const click = useClick(context, {
        enabled: !snapshot.disabled && !isLoading && !isControlled,
    });
    const dismiss = useDismiss(context, {
        enabled: !disableClickOutside,
    });
    const {getReferenceProps, getFloatingProps} = useInteractions([click, dismiss]);

    // Auto-close when date range is fully selected
    useEffect(() => {
        const wasEmpty = !prevHasValueRef.current;
        const nowFull = snapshot.hasValue;
        if (isOpen && wasEmpty && nowFull) {
            setIsOpen(false);
        }

        prevHasValueRef.current = snapshot.hasValue;
    }, [snapshot.hasValue, isOpen]);

    // Handlers
    const handleDayClick = useCallback(
        (day: CalendarDay): void => {
            model.selectDay(day);
        },
        [model],
    );

    const handleDayHover = useCallback(
        (date: Date): void => {
            model.setHoverDate(date);
        },
        [model],
    );

    const handleDayLeave = useCallback((): void => {
        model.clearHoverDate();
    }, [model]);

    const handleClear = useCallback((): void => {
        setIsOpen(false);
        model.clear();
    }, [model]);

    const formatDisplayValue = useCallback((date: Date | null): string => {
        if (!date) return "";
        return date.toLocaleDateString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    }, []);

    return {
        snapshot,
        isOpen,
        refs,
        floatingStyles,
        getReferenceProps,
        getFloatingProps,
        handleDayClick,
        handleDayHover,
        handleDayLeave,
        handleClear,
        formatDisplayValue,
    };
}
